"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "@/lib/api";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "lucide-react";
import type { HookData } from "jspdf-autotable";

type JsPDFWithAutoTable = jsPDF & { lastAutoTable?: { finalY: number } };

/* ----------------------------- Cloudinary helpers ---------------------------- */
const stripFlags = (u: string) =>
  u.replace(/\/upload\/fl_[^/]+\/?/i, "/upload/");
const stripForcedFormat = (u: string) =>
  u.replace(/(\/upload\/v\d+\/[^?]+?)\.[a-z0-9]+(\?.*)?$/i, "$1$2");
const normalizeCloudinaryUrl = (u: string) => stripForcedFormat(stripFlags(u));
const toAttachment = (u: string) =>
  u.includes("/upload/fl_attachment/")
    ? u
    : u.replace("/upload/", "/upload/fl_attachment/");
const looksPdfFromUrl = (u: string) =>
  /\.pdf($|\?)/i.test(u) || /\/raw\/upload\//i.test(u);

/* ------------------------------- Region helpers ------------------------------ */
const REGION_KEY_TO_LABEL: Record<string, string> = {
  bt: "Southern",
  eastern: "Eastern",
  central: "Central",
  northern: "Northern",
};
const labelRegion = (b: Booking) =>
  b.regionName ||
  REGION_KEY_TO_LABEL[b.regionKey || ""] ||
  REGION_KEY_TO_LABEL[b.region || ""] ||
  (b.region ? b.region : "—");

/* ---------------------------------- Types ---------------------------------- */
interface BookingDelivery {
  outsideBlantyre?: boolean;
  negotiatedDeliveryFee?: number;
  baseDeliveryFeePercent?: number;
  policyAcceptedAt?: string;
  viewedRepairItemsAt?: string;
}

interface Booking {
  _id: string;
  car: string;
  customer: string;
  phone: string;
  email: string;

  regionKey?: "bt" | "eastern" | "central" | "northern";
  regionName?: "Southern" | "Eastern" | "Central" | "Northern";
  district: string;
  pickupMode?: "branch" | "delivery";
  region?: string;

  location: string;
  paymentMethod: string;

  unitPrice?: number;
  days?: number;
  baseAmount?: number;
  deliveryFee?: number;
  totalAmount: number;

  delivery?: BookingDelivery;

  licenseUrl?: string;
  idUrl?: string;
  signedPolicyUrl?: string;

  dateBooked: string;
  returnDate: string;

  status: "Pending" | "Approved" | "Rejected" | "Posted" | "Expired";
}

interface Car {
  _id: string;
  name: string;
}

/* --------------------------- Delivery presentation -------------------------- */
function inferDeliveryState(b: Booking) {
  if (b.pickupMode !== "delivery")
    return { label: "—", tone: "muted" as const };

  const base = Number(b.baseAmount || 0);
  const fee = Number(b.deliveryFee || 0);
  const pct10 = Math.round(base * 0.1);
  const outside = Boolean(b.delivery?.outsideBlantyre);

  if (outside) {
    if (fee > 0) return { label: "Outside (set)", tone: "ok" as const };
    return { label: "Outside (pending)", tone: "warn" as const };
  }

  if (fee > 0 && base > 0 && Math.abs(fee - pct10) <= 1) {
    return { label: "Inside (10%)", tone: "ok" as const };
  }

  return {
    label: fee > 0 ? `Fee: MK${fee.toLocaleString("en-MW")}` : "Delivery",
    tone: "muted" as const,
  };
}

/* -------------------------------- Component -------------------------------- */
const BookingTable = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const PAGE_SIZE = 6;
  const [page, setPage] = useState(1);

  // Preview state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocUrl, setSelectedDocUrl] = useState<string | null>(null);
  const [selectedDocType, setSelectedDocType] = useState<
    "pdf" | "image" | null
  >(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  // Delete confirm modal
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showDelete, setShowDelete] = useState(false);

  // Negotiate delivery modal
  const [negDialogOpen, setNegDialogOpen] = useState(false);
  const [negBooking, setNegBooking] = useState<Booking | null>(null);
  const [negValue, setNegValue] = useState<string>("0");
  const [negSaving, setNegSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bookingRes, carRes] = await Promise.all([
          axios.get<Booking[]>("/bookings"),
          axios.get<Car[]>("/cars"),
        ]);
        const bookingData: Booking[] = Array.isArray(bookingRes.data)
          ? bookingRes.data
          : [];
        const carData: Car[] = Array.isArray(carRes.data) ? carRes.data : [];
        setBookings(bookingData);
        setCars(carData);
        setPage(1);
      } catch (err: unknown) {
        toast.error("Failed to load bookings or cars.");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Revoke blob URL when it changes/unmounts
  useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    };
  }, [previewBlobUrl]);

  // Car lookup
  const carMap = useMemo(
    () =>
      cars.reduce((map, car) => {
        map[car._id] = car.name;
        return map;
      }, {} as Record<string, string>),
    [cars]
  );

  const getCarName = useCallback(
    (carId: string) => carMap[carId] || "Unknown",
    [carMap]
  );

  // Derived pagination
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(bookings.length / PAGE_SIZE)),
    [bookings.length]
  );
  const pagedBookings = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return bookings.slice(start, start + PAGE_SIZE);
  }, [bookings, page]);
  const goToPage = (p: number) => setPage(Math.max(1, Math.min(totalPages, p)));
  const nextPage = () => goToPage(page + 1);
  const prevPage = () => goToPage(page - 1);

  const handleStatusChange = async (
    pageIndex: number,
    newStatus: Booking["status"]
  ) => {
    const absIndex = (page - 1) * PAGE_SIZE + pageIndex;
    const booking = bookings[absIndex];
    setActionLoadingId(booking._id);
    try {
      type UpdateStatusResponse = {
        message: string;
        booking: Booking;
        emailSent?: boolean;
        emailError?: string;
      };

      const { data } = await axios.patch<UpdateStatusResponse>(
        `/bookings/${booking._id}/status`,
        { status: newStatus }
      );

      setBookings((prev) => {
        const copy = [...prev];
        copy[absIndex] = { ...copy[absIndex], status: newStatus };
        return copy;
      });

      if (newStatus === "Approved") {
        if (data?.emailSent) {
          toast.success(
            "Booking approved. Confirmation email sent to the customer."
          );
        } else if (booking.email) {
          toast.success("Booking approved. (Email not sent)");
          if (data?.emailError) toast.error(`Email error: ${data.emailError}`);
        } else {
          toast.success("Booking approved. No customer email on file.");
        }
      } else if (newStatus === "Posted") {
        toast.success("Car marked as returned and available.");
      } else if (newStatus === "Rejected") {
        toast.success("Booking rejected.");
      } else if (newStatus === "Expired") {
        toast.success("Booking expired.");
      } else {
        toast.success(`Booking ${newStatus.toLowerCase()}.`);
      }
    } catch (error) {
      toast.error("Failed to update booking status.");
      console.error("Update error:", error);
    } finally {
      setActionLoadingId(null);
    }
  };

  // Open negotiate modal
  const openNegotiate = (b: Booking) => {
    setNegBooking(b);
    setNegValue(String(b.deliveryFee ?? 0));
    setNegDialogOpen(true);
  };

  // Save negotiated fee
  const saveNegotiatedFee = async () => {
    if (!negBooking) return;
    const id = negBooking._id;
    const base = Number(negBooking.baseAmount || 0);

    const feeNum = Number(negValue);
    if (Number.isNaN(feeNum) || feeNum < 0) {
      toast.error("Please enter a valid non-negative fee.");
      return;
    }

    setNegSaving(true);
    try {
      const payload = {
        delivery: { outsideBlantyre: true, negotiatedDeliveryFee: feeNum },
        deliveryFee: feeNum,
        totalAmount: base + feeNum,
      };
      await axios.patch(`/bookings/${id}`, payload);

      setBookings((prev) =>
        prev.map((b) =>
          b._id === id
            ? {
                ...b,
                delivery: {
                  ...(b.delivery || {}),
                  outsideBlantyre: true,
                  negotiatedDeliveryFee: feeNum,
                },
                deliveryFee: feeNum,
                totalAmount: base + feeNum,
              }
            : b
        )
      );

      toast.success("Delivery fee updated.");
      setNegDialogOpen(false);
      setNegBooking(null);
    } catch (err: any) {
      console.error("Set fee failed:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        "Failed to set delivery fee.";
      toast.error(msg);
    } finally {
      setNegSaving(false);
    }
  };

  // Delete flow
  const handleDelete = (bookingId: string) => {
    setDeleteId(bookingId);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setActionLoadingId(deleteId);
    try {
      await axios.delete(`/bookings/${deleteId}`);
      setBookings((prev) => prev.filter((b) => b._id !== deleteId));
      setPage((prev) => {
        const newTotalPages = Math.max(
          1,
          Math.ceil((bookings.length - 1) / PAGE_SIZE)
        );
        return Math.min(prev, newTotalPages);
      });
      toast.success("Booking deleted.");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete booking.");
    } finally {
      setActionLoadingId(null);
      setShowDelete(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setShowDelete(false);
    setDeleteId(null);
  };

  /* ------------------------------- PDF exports ------------------------------ */
  const addPDFHeader = (doc: jsPDF, title: string) => {
    doc.setFillColor(59, 130, 246);
    doc.rect(14, 10, 180, 25, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("EMBRACE RENTALS", 20, 25);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Professional Car Rental Services", 20, 30);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(title, 14, 45);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 52);
    return 60;
  };

  const addPDFFooter = (doc: jsPDF, pageNumber: number, totalPages: number) => {
    const pageHeight = doc.internal.pageSize.height;
    doc.setDrawColor(200, 200, 200);
    doc.line(14, pageHeight - 20, 196, pageHeight - 20);
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(
      "Embrace Rentals | Professional Car Rental Services",
      14,
      pageHeight - 12
    );
    doc.text(
      "Contact: support@embracerentals.com | Phone: +265 888 209 399",
      14,
      pageHeight - 8
    );
    doc.setTextColor(0, 0, 0);
    doc.text(`Page ${pageNumber} of ${totalPages}`, 196 - 30, pageHeight - 8);
  };

  const totalRevenue = (rows: Booking[]) =>
    rows.reduce((sum, b) => sum + (Number(b.totalAmount) || 0), 0);

  const exportAllToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm" });
    const startY = addPDFHeader(doc, "All Bookings Report");

    const totalBookings = bookings.length;
    const pendingCount = bookings.filter((b) => b.status === "Pending").length;
    const approvedCount = bookings.filter(
      (b) => b.status === "Approved"
    ).length;
    const rejectedCount = bookings.filter(
      (b) => b.status === "Rejected"
    ).length;
    const postedCount = bookings.filter((b) => b.status === "Posted").length;
    const expiredCount = bookings.filter((b) => b.status === "Expired").length;
    const revenue = totalRevenue(bookings);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Summary Statistics:", 14, startY + 5);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Total Bookings: ${totalBookings}`, 14, startY + 15);
    doc.text(`Pending: ${pendingCount}`, 14, startY + 22);
    doc.text(`Approved: ${approvedCount}`, 60, startY + 22);
    doc.text(`Rejected: ${rejectedCount}`, 110, startY + 22);
    doc.text(`Posted: ${postedCount}`, 160, startY + 22);
    doc.text(`Expired: ${expiredCount}`, 14, startY + 29);
    doc.text(
      `Total Revenue: MK${revenue.toLocaleString("en-MW")}`,
      60,
      startY + 29
    );

    const head = [
      [
        "Car",
        "Customer",
        "Phone",
        "Email",
        "Booked",
        "Return",
        "Region",
        "District",
        "Pickup",
        "Payment",
        "Unit",
        "Days",
        "Deliv.",
        "Total",
        "Documents",
        "Policy",
        "Status",
        "Delivery",
      ],
    ];

    const body = bookings.map((b) => {
      const dv = inferDeliveryState(b).label;
      return [
        getCarName(b.car),
        b.customer,
        b.phone,
        b.email || "—",
        new Date(b.dateBooked).toLocaleDateString(),
        new Date(b.returnDate).toLocaleDateString(),
        labelRegion(b),
        b.district,
        b.pickupMode === "delivery" ? "Delivery" : "Branch",
        b.paymentMethod,
        b.unitPrice != null
          ? `MK${(b.unitPrice || 0).toLocaleString("en-MW")}`
          : "—",
        b.days ?? "—",
        b.deliveryFee != null
          ? `MK${(b.deliveryFee || 0).toLocaleString("en-MW")}`
          : "—",
        `MK${(b.totalAmount || 0).toLocaleString("en-MW")}`,
        b.licenseUrl || b.idUrl ? "Yes" : "—",
        b.signedPolicyUrl ? "Yes" : "—",
        b.status,
        dv,
      ];
    });

    autoTable(doc, {
      head,
      body,
      startY: startY + 40,
      theme: "grid",
      margin: { left: 10, right: 10 },
      tableWidth: "wrap",
      styles: {
        fontSize: 8,
        cellPadding: 2.5,
        overflow: "linebreak",
        valign: "middle",
        minCellHeight: 6,
      },
      headStyles: {
        fontSize: 9,
        fontStyle: "bold",
        fillColor: [59, 130, 246],
        textColor: 255,
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 24 },
        2: { cellWidth: 22 },
        3: { cellWidth: 34 },
        4: { cellWidth: 16, halign: "center" },
        5: { cellWidth: 16, halign: "center" },
        6: { cellWidth: 18 },
        7: { cellWidth: 16 },
        8: { cellWidth: 16 },
        9: { cellWidth: 18, halign: "right" },
        10: { cellWidth: 10, halign: "center" },
        11: { cellWidth: 18, halign: "right" },
        12: { cellWidth: 20, halign: "right" },
        13: { cellWidth: 20, halign: "right" },
        14: { cellWidth: 16, halign: "center" },
        15: { cellWidth: 12, halign: "center" },
        16: { cellWidth: 16, halign: "center" },
        17: { cellWidth: 28, halign: "left" },
      },
      didDrawPage: (data: HookData) => {
        const pageNumber = data.pageNumber;
        const totalPages =
          typeof doc.getNumberOfPages === "function"
            ? doc.getNumberOfPages()
            : 1;
        addPDFFooter(doc, pageNumber, totalPages);
      },
    });

    doc.save("embrace_all_bookings_report.pdf");
    toast.success("PDF report exported successfully!");
  };

  const exportSingleToPDF = (b: Booking) => {
    const doc = new jsPDF();
    const startY = addPDFHeader(doc, "Booking Receipt");

    autoTable(doc, {
      head: [["Field", "Information"]],
      body: [
        ["Booking ID", b._id],
        ["Car", getCarName(b.car)],
        ["Customer Name", b.customer],
        ["Phone Number", b.phone],
        ["Email Address", b.email || "—"],
        ["Region", labelRegion(b)],
        ["District", b.district],
        [
          "Pickup Mode",
          b.pickupMode === "delivery"
            ? "Delivery to Location"
            : "Pickup at Branch",
        ],
        [
          "Unit Price",
          b.unitPrice != null
            ? `MK${(b.unitPrice || 0).toLocaleString("en-MW")}`
            : "—",
        ],
        ["Days", b.days ?? "—"],
        [
          "Base Amount",
          b.baseAmount != null
            ? `MK${(b.baseAmount || 0).toLocaleString("en-MW")}`
            : "—",
        ],
        [
          "Delivery Fee",
          b.deliveryFee != null
            ? `MK${(b.deliveryFee || 0).toLocaleString("en-MW")}`
            : "—",
        ],
        ["Total Amount", `MK${(b.totalAmount || 0).toLocaleString("en-MW")}`],
        ["Payment Method", b.paymentMethod],
        ["Date Booked", new Date(b.dateBooked).toLocaleString()],
        ["Return Date", new Date(b.returnDate).toLocaleString()],
        ["License Document", b.licenseUrl ? "Provided" : "Not Provided"],
        ["National ID Document", b.idUrl ? "Provided" : "Not Provided"],
        ["Signed Policy", b.signedPolicyUrl ? "Provided" : "Not Provided"],
        ["Status", b.status],
      ],
      startY: startY + 15,
      styles: { fontSize: 10, cellPadding: 4 },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontSize: 11,
        fontStyle: "bold",
      },
      columnStyles: { 0: { fontStyle: "bold", fillColor: [248, 250, 252] } },
      didDrawPage: (data: HookData) => {
        const pageNumber = data.pageNumber;
        const totalPages =
          typeof (doc as jsPDF).getNumberOfPages === "function"
            ? doc.getNumberOfPages()
            : 1;
        addPDFFooter(doc, pageNumber, totalPages);
      },
    });

    const finalY =
      ((doc as JsPDFWithAutoTable).lastAutoTable?.finalY ?? startY + 15) + 20;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    [
      "• Valid driving license required at all times during rental period",
      "• Customer is responsible for any damages or traffic violations",
      "• Late return charges apply after grace period",
      "• Full payment required before vehicle handover",
    ].forEach((term, i) => doc.text(term, 14, finalY + 10 + i * 5));

    doc.save(`booking_receipt_${b.customer.replace(/\s+/g, "_")}.pdf`);
    toast.success("Receipt exported successfully!");
  };

  /* ------------------------ Robust Blob-based preview ----------------------- */
  async function openPreviewBlob(originalUrl: string) {
    const cleanUrl = normalizeCloudinaryUrl(originalUrl);

    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewBlobUrl(null);

    try {
      let mime = "";
      try {
        const head = await fetch(cleanUrl, { method: "HEAD" });
        mime = head.headers.get("content-type") || "";
      } catch {
        // ignore
      }

      const res = await fetch(cleanUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const isPdf = mime.includes("pdf") || looksPdfFromUrl(cleanUrl);

      const finalBlob =
        isPdf && blob.type !== "application/pdf"
          ? new Blob([await blob.arrayBuffer()], { type: "application/pdf" })
          : blob;

      const url = URL.createObjectURL(finalBlob);
      setPreviewBlobUrl(url);
      setSelectedDocType(isPdf ? "pdf" : "image");
      setSelectedDocUrl(cleanUrl);
      setModalOpen(true);
    } catch (e: unknown) {
      const message =
        e instanceof Error
          ? e.message
          : typeof e === "string"
          ? e
          : "Failed to load document.";
      setPreviewError(message);
      setSelectedDocUrl(cleanUrl);
      setModalOpen(true);
    } finally {
      setPreviewLoading(false);
    }
  }

  if (loading)
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-8 h-8 border-b-2 border-blue-800 rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-600">Loading bookings...</span>
      </div>
    );

  return (
    <>
      {/* --- Compact styles to mimic System Audit density --- */}
      <style>{`
        .table-compact th { padding: 8px 10px; font-size: 11.5px; }
        .table-compact td { padding: 6px 10px; font-size: 13px; white-space: nowrap; vertical-align: middle; }
        .toolbar-compact { padding: 4px 10px; }
        .btn-compact { height: 30px; padding: 0 10px; font-size: 12px; }
      `}</style>

      {/* top toolbar (tight like audit page) */}
      <div className="flex items-center justify-between mx-3 mt-0 mb-1 toolbar-compact">
        <div className="text-[13px] text-gray-600">
          Total Bookings:{" "}
          <span className="font-semibold">{bookings.length}</span>
        </div>
        <button
          onClick={exportAllToPDF}
          className="text-white transition-colors bg-blue-800 rounded shadow-sm btn-compact hover:bg-blue-700 disabled:opacity-60"
          disabled={bookings.length === 0}
        >
          Export All to PDF ({bookings.length})
        </button>
      </div>

      {/* table card */}
      <div className="mx-3 overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
        <table className="w-full leading-tight text-left text-gray-700 table-auto table-compact">
          <thead className="bg-blue-50 text-[11px] uppercase text-blue-900">
            <tr className="border-b border-blue-100">
              <th className="px-3">Car</th>
              <th className="px-3">Customer</th>
              <th className="px-3">Phone</th>
              <th className="px-3">Email</th>
              <th className="px-3">Booked</th>
              <th className="px-3">Return</th>
              <th className="px-3">Region</th>
              <th className="px-3">District</th>
              <th className="px-3">Pickup</th>
              <th className="px-3">Payment</th>
              <th className="px-3">Unit Price</th>
              <th className="px-3">Days</th>
              <th className="px-3">Deliv. Fee</th>
              <th className="px-3">Total</th>
              <th className="px-3">Documents</th>
              <th className="px-3">Policy</th>
              <th className="px-3">Status</th>
              <th className="px-3">Delivery</th>
              <th className="px-3">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {bookings.length === 0 ? (
              <tr>
                <td colSpan={19} className="p-8 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <div className="mb-2 text-gray-400">📋</div>
                    <div>No bookings found</div>
                    <div className="mt-1 text-xs text-gray-400">
                      Bookings will appear here once they are created
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              pagedBookings.map((b, i) => {
                const dv = inferDeliveryState(b);
                const canNegotiate =
                  b.status === "Pending" &&
                  b.pickupMode === "delivery" &&
                  (b.delivery?.outsideBlantyre === true ||
                    Number(b.deliveryFee || 0) === 0);

                return (
                  <tr
                    key={b._id}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="px-3 font-medium">{getCarName(b.car)}</td>
                    <td className="px-3">{b.customer}</td>
                    <td className="px-3">{b.phone}</td>
                    <td className="px-3 max-w-[180px] truncate" title={b.email}>
                      {b.email || "—"}
                    </td>
                    <td className="px-3">
                      {new Date(b.dateBooked).toLocaleDateString()}
                    </td>
                    <td className="px-3">
                      {new Date(b.returnDate).toLocaleDateString()}
                    </td>

                    <td className="px-3">{labelRegion(b)}</td>
                    <td className="px-3">{b.district}</td>
                    <td className="px-3">
                      {b.pickupMode === "delivery" ? "Delivery" : "Branch"}
                    </td>

                    <td className="px-3">{b.paymentMethod}</td>
                    <td className="px-3">
                      {b.unitPrice != null
                        ? `MK${(b.unitPrice || 0).toLocaleString("en-MW")}`
                        : "—"}
                    </td>
                    <td className="px-3">{b.days ?? "—"}</td>
                    <td className="px-3">
                      {b.deliveryFee != null
                        ? `MK${(b.deliveryFee || 0).toLocaleString("en-MW")}`
                        : "—"}
                    </td>
                    <td className="px-3 font-semibold">
                      MK{(b.totalAmount || 0).toLocaleString("en-MW")}
                    </td>

                    {/* Documents */}
                    <td className="px-3">
                      <div className="flex items-center gap-2">
                        {b.licenseUrl && (
                          <button
                            onClick={() => openPreviewBlob(b.licenseUrl!)}
                            className="text-[12px] text-blue-600 hover:underline"
                          >
                            View License
                          </button>
                        )}
                        {b.idUrl && (
                          <button
                            onClick={() => openPreviewBlob(b.idUrl!)}
                            className="text-[12px] text-blue-600 hover:underline"
                          >
                            View ID
                          </button>
                        )}
                        {!b.licenseUrl && !b.idUrl && (
                          <span className="text-[12px] text-gray-400">
                            None
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Signed Policy */}
                    <td className="px-3">
                      {b.signedPolicyUrl ? (
                        <div className="flex items-center gap-2">
                          <svg
                            viewBox="0 0 24 24"
                            aria-hidden
                            className="w-4 h-4 text-red-600"
                            fill="currentColor"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm0 0v6h6" />
                            <path d="M8.5 15.5h1.1c.9 0 1.4-.5 1.4-1.2 0-.8-.6-1.2-1.2-1.2H8.5v2.4zm0-3.6h1.1c.8 0 1.3-.4 1.3-1.1s-.5-1-1.3-1H8.5v2.1zM12.8 10.6h1.7c.9 0 1.6.5 1.6 1.5s-.7 1.6-1.6 1.6h-.8v1.8h-.9zM17.9 10.6h2.1v.8h-1.2v.9h1.1v.8h-1.1v1.9h-.9z" />
                          </svg>
                          <button
                            onClick={() => openPreviewBlob(b.signedPolicyUrl!)}
                            className="text-[12px] text-blue-600 hover:underline"
                          >
                            View
                          </button>
                          <a
                            href={toAttachment(b.signedPolicyUrl!)}
                            className="inline-flex items-center rounded px-1.5 py-0.5"
                            title="Download"
                          >
                            <Download className="w-4 h-4 text-blue-500" />
                          </a>
                        </div>
                      ) : (
                        <span className="text-[12px] text-gray-400">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          b.status === "Approved"
                            ? "bg-green-100 text-green-800"
                            : b.status === "Pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : b.status === "Rejected"
                            ? "bg-red-100 text-red-800"
                            : b.status === "Posted"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {b.status}
                      </span>
                    </td>

                    {/* Delivery */}
                    <td className="px-3">
                      <span
                        className={`text-[12px] font-medium ${
                          dv.tone === "ok"
                            ? "text-emerald-700"
                            : dv.tone === "warn"
                            ? "text-amber-700"
                            : "text-slate-500"
                        }`}
                      >
                        {dv.label}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          onClick={() => exportSingleToPDF(b)}
                          className="inline-flex items-center justify-center text-indigo-700 rounded btn-compact hover:text-indigo-900 hover:underline"
                        >
                          Export
                        </button>

                        {b.status === "Pending" && (
                          <>
                            <button
                              onClick={() => handleStatusChange(i, "Approved")}
                              disabled={actionLoadingId === b._id}
                              className={`btn-compact inline-flex items-center justify-center rounded ${
                                actionLoadingId === b._id
                                  ? "cursor-not-allowed text-gray-400"
                                  : "text-green-700 hover:text-green-900 hover:underline"
                              }`}
                            >
                              {actionLoadingId === b._id
                                ? "Approving…"
                                : "Approve"}
                            </button>

                            <button
                              onClick={() => handleStatusChange(i, "Rejected")}
                              disabled={actionLoadingId === b._id}
                              className={`btn-compact inline-flex items-center justify-center rounded ${
                                actionLoadingId === b._id
                                  ? "cursor-not-allowed text-gray-400"
                                  : "text-red-600 hover:text-red-800 hover:underline"
                              }`}
                            >
                              {actionLoadingId === b._id
                                ? "Rejecting…"
                                : "Reject"}
                            </button>
                          </>
                        )}

                        {b.status === "Approved" && (
                          <button
                            onClick={() => handleStatusChange(i, "Posted")}
                            disabled={actionLoadingId === b._id}
                            className={`btn-compact inline-flex items-center justify-center rounded ${
                              actionLoadingId === b._id
                                ? "cursor-not-allowed text-gray-400"
                                : "text-blue-900 hover:text-blue-700 hover:underline"
                            }`}
                          >
                            {actionLoadingId === b._id
                              ? "Updating…"
                              : "Mark Returned"}
                          </button>
                        )}

                        {(b.status === "Posted" || b.status === "Expired") && (
                          <span className="inline-flex items-center text-[12px] italic text-gray-500">
                            {b.status}
                          </span>
                        )}

                        {canNegotiate && (
                          <button
                            onClick={() => openNegotiate(b)}
                            className="inline-flex items-center justify-center rounded btn-compact text-amber-700 hover:text-amber-900 hover:underline"
                            title="Set negotiated delivery fee"
                          >
                            Set Delivery Fee
                          </button>
                        )}

                        <button
                          onClick={() => handleDelete(b._id)}
                          disabled={actionLoadingId === b._id}
                          className={`btn-compact inline-flex items-center justify-center rounded ${
                            actionLoadingId === b._id
                              ? "cursor-not-allowed text-gray-400"
                              : "text-rose-700 hover:text-rose-900 hover:underline"
                          }`}
                          title="Delete booking"
                        >
                          {actionLoadingId === b._id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination (compact) */}
      <div className="flex items-center justify-between mx-3 mt-2">
        <div className="text-[12px] text-gray-600">
          {bookings.length > 0 ? (
            <>
              Showing{" "}
              <span className="font-semibold">
                {(page - 1) * PAGE_SIZE + 1}
              </span>{" "}
              –{" "}
              <span className="font-semibold">
                {Math.min(page * PAGE_SIZE, bookings.length)}
              </span>{" "}
              of <span className="font-semibold">{bookings.length}</span>
            </>
          ) : (
            "No results"
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={prevPage}
            disabled={page === 1}
            className={`rounded border px-2.5 py-1 text-[12px] ${
              page === 1
                ? "cursor-not-allowed bg-gray-50 text-gray-400"
                : "border-blue-200 text-blue-700 hover:bg-blue-50"
            }`}
          >
            Prev
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(
              (p) => (
                <button
                  key={p}
                  onClick={() => goToPage(p)}
                  className={`rounded px-2.5 py-1 text-[12px] ${
                    p === page
                      ? "bg-blue-700 text-white"
                      : "border border-blue-200 text-blue-700 hover:bg-blue-50"
                  }`}
                >
                  {p}
                </button>
              )
            )}
          </div>

          <button
            onClick={nextPage}
            disabled={page === totalPages}
            className={`rounded border px-2.5 py-1 text-[12px] ${
              page === totalPages
                ? "cursor-not-allowed bg-gray-50 text-gray-400"
                : "border-blue-200 text-blue-700 hover:bg-blue-50"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md p-5 bg-white rounded-lg shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-red-700">
              Delete Booking
            </h3>
            <p className="text-sm text-gray-700">
              Are you sure you want to delete this booking? This action cannot
              be undone.
            </p>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={!!actionLoadingId}
                className={`rounded px-4 py-2 text-sm text-white ${
                  actionLoadingId
                    ? "cursor-not-allowed bg-red-300"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {actionLoadingId ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Negotiate Delivery Fee Modal */}
      {negDialogOpen && negBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md p-5 bg-white rounded-lg shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-amber-800">
              Set Delivery Fee (Outside Blantyre)
            </h3>
            <p className="text-sm text-gray-600">
              Enter the negotiated delivery fee for{" "}
              <span className="font-medium">{getCarName(negBooking.car)}</span>.
              This will be added to the base amount to compute the total.
            </p>

            <div className="mt-4">
              <label className="block mb-1 text-sm text-gray-700">
                Delivery Fee (MWK)
              </label>
              <input
                type="number"
                min={0}
                step="1"
                value={negValue}
                onChange={(e) => setNegValue(e.target.value)}
                className="w-full p-2 border rounded"
              />
              {negBooking.baseAmount != null && (
                <p className="mt-2 text-xs text-gray-500">
                  Base:{" "}
                  <span className="font-medium">
                    MK{negBooking.baseAmount.toLocaleString("en-MW")}
                  </span>{" "}
                  → New Total:{" "}
                  <span className="font-medium">
                    MK
                    {(
                      Number(negValue || 0) + Number(negBooking.baseAmount || 0)
                    ).toLocaleString("en-MW")}
                  </span>
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setNegDialogOpen(false)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveNegotiatedFee}
                disabled={negSaving}
                className={`rounded px-4 py-2 text-sm text-white ${
                  negSaving
                    ? "cursor-not-allowed bg-amber-300"
                    : "bg-amber-600 hover:bg-amber-700"
                }`}
              >
                {negSaving ? "Saving…" : "Save Fee"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="relative w-full max-w-3xl p-4 bg-white rounded shadow-lg">
            <button
              onClick={() => {
                setModalOpen(false);
                setPreviewError(null);
                if (previewBlobUrl) {
                  URL.revokeObjectURL(previewBlobUrl);
                  setPreviewBlobUrl(null);
                }
              }}
              className="absolute text-gray-600 right-2 top-2 hover:text-black"
            >
              ✕
            </button>
            <h2 className="mb-3 text-lg font-semibold text-blue-900">
              Document Preview
            </h2>

            {previewLoading ? (
              <div className="p-6 text-sm text-gray-600">Loading preview…</div>
            ) : previewError ? (
              <div className="p-6 text-sm text-red-600">
                Preview failed: {previewError}.{" "}
                {selectedDocUrl && (
                  <a
                    className="underline"
                    href={selectedDocUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in new tab
                  </a>
                )}
              </div>
            ) : previewBlobUrl ? (
              selectedDocType === "image" ? (
                <img
                  src={previewBlobUrl}
                  alt="Document"
                  className="max-h-[70vh] w-full rounded border object-contain"
                />
              ) : (
                <iframe
                  src={previewBlobUrl}
                  title="PDF Preview"
                  className="h-[70vh] w-full border"
                />
              )
            ) : (
              <div className="p-6 text-sm text-gray-500">
                No document loaded.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BookingTable;
